import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import CareerDetailModal from '../components/CareerDetailModal';
import { Search, Filter, Briefcase, MapPin, Star, ChevronDown, ChevronUp, X, Eye, Scale, Check, Heart, Sparkles, Palette, Laptop, Stethoscope, Users, GraduationCap, Shield, Dumbbell, Wrench, FlaskConical, Building2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Get or create user session ID for favorites
const getUserSession = () => {
  let sessionId = localStorage.getItem('declic_user_session');
  if (!sessionId) {
    sessionId = 'user_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
    localStorage.setItem('declic_user_session', sessionId);
  }
  return sessionId;
};

export default function ExploreCareers() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useLanguage();
  const [careers, setCareers] = useState([]);
  const [filteredCareers, setFilteredCareers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDomain, setSelectedDomain] = useState('all');
  const [expandedCareer, setExpandedCareer] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [modalCareer, setModalCareer] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  // Compare feature state
  const [compareMode, setCompareMode] = useState(false);
  const [selectedForCompare, setSelectedForCompare] = useState([]);
  
  // Favorites feature state
  const [favorites, setFavorites] = useState([]);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const userSession = getUserSession();
  
  // Interest-based search state
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [interestCareerIds, setInterestCareerIds] = useState([]);
  const [activeDiscoveryCard, setActiveDiscoveryCard] = useState(null);
  const searchRef = useRef(null);
  const suggestionsRef = useRef(null);

  const domains = [
    { id: 'all', label: language === 'fr' ? 'Tous les domaines' : 'Alle domeinen' },
    { id: 'health', label: language === 'fr' ? 'Santé' : 'Gezondheid' },
    { id: 'technology', label: language === 'fr' ? 'Technologie' : 'Technologie' },
    { id: 'technical', label: language === 'fr' ? 'Technique' : 'Technisch' },
    { id: 'science', label: language === 'fr' ? 'Science' : 'Wetenschap' },
    { id: 'creativity', label: language === 'fr' ? 'Créativité' : 'Creativiteit' },
    { id: 'education', label: language === 'fr' ? 'Éducation' : 'Onderwijs' },
    { id: 'social', label: language === 'fr' ? 'Social' : 'Sociaal' },
    { id: 'business', label: language === 'fr' ? 'Business' : 'Business' },
    { id: 'administration', label: language === 'fr' ? 'Gestion & Organisation' : 'Beheer & Organisatie' },
    { id: 'sports', label: language === 'fr' ? 'Sport & Bien-être' : 'Sport & Welzijn' },
  ];

  useEffect(() => {
    fetchCareers();
    fetchFavorites();
    // Restore selection from navigation state (when returning from compare page)
    if (location.state?.selectedForCompare) {
      setSelectedForCompare(location.state.selectedForCompare);
      setCompareMode(true);
    }
    
    // Close suggestions when clicking outside
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target) &&
          searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [language]);

  useEffect(() => {
    // Only filter when careers are loaded
    if (careers.length > 0) {
      filterCareers();
    } else {
      setFilteredCareers([]);
    }
  }, [searchTerm, selectedDomain, careers, showFavoritesOnly, favorites, interestCareerIds]);

  // Fetch search suggestions (debounced)
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (searchTerm.length >= 2) {
        try {
          const response = await axios.get(`${API}/search/suggestions/${language}?q=${encodeURIComponent(searchTerm)}`);
          setSuggestions(response.data.suggestions || []);
          setShowSuggestions(true);
        } catch (error) {
          console.error('Error fetching suggestions:', error);
        }
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    };

    const timer = setTimeout(fetchSuggestions, 200);
    return () => clearTimeout(timer);
  }, [searchTerm, language]);

  // Fetch careers by interest when search term matches an interest
  useEffect(() => {
    const fetchInterestCareers = async () => {
      if (searchTerm.length >= 3) {
        try {
          const response = await axios.get(`${API}/search/by-interest/${language}?interest=${encodeURIComponent(searchTerm)}`);
          setInterestCareerIds(response.data.career_ids || []);
        } catch (error) {
          console.error('Error fetching interest careers:', error);
          setInterestCareerIds([]);
        }
      } else {
        setInterestCareerIds([]);
      }
    };

    const timer = setTimeout(fetchInterestCareers, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, language]);

  const fetchCareers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/careers/${language}`);
      const careersList = response.data.careers || [];
      setCareers(careersList);
      setFilteredCareers(careersList); // Initialize filtered with all careers
      setLoading(false);
    } catch (error) {
      console.error('Error fetching careers:', error);
      setLoading(false);
    }
  };

  // Fetch user's favorites
  const fetchFavorites = async () => {
    try {
      const response = await axios.get(`${API}/favorites/${userSession}?language=${language}`);
      const favoriteIds = (response.data.favorites || []).map(f => f.career_id);
      setFavorites(favoriteIds);
    } catch (error) {
      console.error('Error fetching favorites:', error);
    }
  };

  // Toggle favorite status for a career
  const toggleFavorite = async (career) => {
    const isFavorite = favorites.includes(career.id);
    
    try {
      if (isFavorite) {
        // Remove from favorites
        await axios.delete(`${API}/favorites/remove/${userSession}/${career.id}`);
        setFavorites(prev => prev.filter(id => id !== career.id));
      } else {
        // Add to favorites
        await axios.post(`${API}/favorites/add`, {
          career_id: career.id,
          career_title: career.title,
          user_session: userSession,
          language: language
        });
        setFavorites(prev => [...prev, career.id]);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  // Check if a career is a favorite
  const isFavorite = (careerId) => favorites.includes(careerId);

  // Normalize text: remove accents and convert to lowercase
  const normalizeText = (text) => {
    if (!text) return '';
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .replace(/[œ]/g, 'oe')
      .replace(/[æ]/g, 'ae');
  };

  // Extended synonyms dictionary for better search (includes accent-free versions)
  const synonyms = {
    // Education
    'enseignant': ['professeur', 'instituteur', 'formateur', 'éducateur', 'pédagogue', 'prof', 'instit'],
    'prof': ['professeur', 'enseignant', 'formateur', 'instituteur'],
    'instit': ['instituteur', 'enseignant', 'professeur'],
    'maitre': ['instituteur', 'professeur', 'enseignant', 'maître'],
    'teacher': ['professeur', 'enseignant', 'instituteur'],
    'educateur': ['éducateur', 'educateur specialise', 'éducateur spécialisé', 'animateur'],
    'formateur': ['enseignant', 'professeur', 'coach', 'trainer'],
    'pedagogique': ['pédagogique', 'enseignement', 'éducation'],
    
    // Health - with accent-free versions
    'medecin': ['médecin', 'docteur', 'généraliste', 'praticien', 'generaliste'],
    'docteur': ['médecin', 'medecin', 'généraliste'],
    'infirmier': ['infirmière', 'soignant', 'aide-soignant', 'infirmiere'],
    'kine': ['kinésithérapeute', 'kiné', 'kinesitherapeute', 'rééducateur', 'reeducateur'],
    'kiné': ['kinésithérapeute', 'kine', 'kinesitherapeute', 'rééducateur'],
    'kinesitherapeute': ['kinésithérapeute', 'kiné', 'kine'],
    'psy': ['psychologue', 'psychiatre', 'thérapeute', 'therapeute', 'psychotherapeute'],
    'psychologue': ['psy', 'thérapeute', 'therapeute'],
    'dentiste': ['chirurgien dentaire', 'orthodontiste'],
    'pharmacien': ['pharmacienne', 'apothicaire'],
    'sage-femme': ['sage femme', 'accoucheuse', 'maïeuticien'],
    'veterinaire': ['vétérinaire', 'veto'],
    'osteopathe': ['ostéopathe', 'chiropracteur'],
    'dieteticien': ['diététicien', 'nutritionniste', 'dietetique'],
    'ergotherapeute': ['ergothérapeute', 'ergo'],
    'logopede': ['logopède', 'orthophoniste'],
    
    // Social work
    'travailleur social': ['assistant social', 'éducateur', 'educateur', 'aide sociale'],
    'assistant social': ['travailleur social', 'as', 'assistante sociale'],
    'as': ['assistant social', 'assistante sociale'],
    'cpas': ['assistant social', 'social', 'aide sociale'],
    'aide sociale': ['assistant social', 'cpas', 'travailleur social'],
    'humanitaire': ['ong', 'cooperation', 'aide', 'solidarité'],
    
    // Tech - with accent-free versions
    'developpeur': ['développeur', 'programmeur', 'codeur', 'dev', 'developer'],
    'développeur': ['developpeur', 'programmeur', 'codeur', 'dev'],
    'dev': ['développeur', 'developpeur', 'programmeur', 'developer', 'front', 'back', 'fullstack'],
    'programmeur': ['développeur', 'developpeur', 'codeur', 'dev'],
    'informaticien': ['développeur', 'developpeur', 'technicien', 'ingénieur', 'it'],
    'it': ['informatique', 'technologie', 'développeur', 'developpeur', 'tech'],
    'web': ['développeur web', 'developpeur web', 'webdesigner', 'frontend', 'backend'],
    'data': ['data analyst', 'data scientist', 'analyste', 'donnees', 'données'],
    'cyber': ['cybersécurité', 'cybersecurite', 'sécurité informatique', 'hacker'],
    'ia': ['intelligence artificielle', 'ai', 'machine learning', 'ingénieur ia'],
    'devops': ['développeur', 'ops', 'cloud', 'infrastructure'],
    'reseau': ['réseau', 'network', 'ingénieur réseau', 'administrateur'],
    
    // Business
    'manager': ['gestionnaire', 'directeur', 'responsable', 'chef', 'management'],
    'chef': ['manager', 'directeur', 'responsable', 'patron'],
    'patron': ['entrepreneur', 'directeur', 'chef', 'gérant', 'gerant'],
    'commercial': ['vendeur', 'business', 'acheteur', 'sales', 'vente'],
    'vendeur': ['commercial', 'représentant', 'representant', 'conseiller vente'],
    'entrepreneur': ['patron', 'gérant', 'gerant', 'business', 'startup', 'chef entreprise'],
    'marketing': ['communication', 'pub', 'publicité', 'publicite', 'digital'],
    'rh': ['ressources humaines', 'recruteur', 'drh', 'grh'],
    'finance': ['financier', 'comptable', 'banquier', 'trader', 'analyste'],
    'consultant': ['conseiller', 'expert', 'conseil'],
    'project manager': ['chef de projet', 'pm', 'gestion de projet'],
    'product': ['product manager', 'chef de produit', 'pm'],
    
    // Creative - with accent-free versions
    'artiste': ['créatif', 'creatif', 'designer', 'graphiste', 'art'],
    'designer': ['graphiste', 'créatif', 'creatif', 'styliste', 'design'],
    'creatif': ['créatif', 'artiste', 'designer', 'graphiste'],
    'graphiste': ['designer', 'infographiste', 'directeur artistique', 'da'],
    'photographe': ['photo', 'vidéaste', 'videaste', 'cameraman'],
    'videaste': ['vidéaste', 'réalisateur', 'realisateur', 'cameraman', 'monteur'],
    'architecte': ['architecture', 'urbaniste', 'intérieur', 'interieur'],
    'musicien': ['musique', 'compositeur', 'producteur', 'dj'],
    'illustrateur': ['dessinateur', 'bd', 'bande dessinée'],
    'styliste': ['mode', 'fashion', 'couturier', 'designer mode'],
    
    // Technical/Manual - with accent-free versions
    'mecanicien': ['mécanicien', 'technicien', 'garagiste', 'réparateur', 'reparateur', 'auto'],
    'électricien': ['electricien', 'technicien', 'installateur', 'electrique'],
    'electricien': ['électricien', 'technicien', 'installateur'],
    'plombier': ['installateur', 'technicien', 'chauffagiste', 'sanitaire'],
    'ouvrier': ['technicien', 'artisan', 'manuel', 'travailleur'],
    'menuisier': ['charpentier', 'ebeniste', 'ébéniste', 'bois'],
    'soudeur': ['métallier', 'metallier', 'chaudronnier'],
    'electromecanicien': ['électromécanicien', 'technicien', 'maintenance'],
    'frigoriste': ['climatisation', 'hvac', 'froid'],
    'carrossier': ['auto', 'véhicule', 'vehicule', 'peintre auto'],
    
    // Engineering
    'ingenieur': ['ingénieur', 'engineer', 'ing'],
    'ingénieur': ['ingenieur', 'engineer', 'ing'],
    'civil': ['génie civil', 'genie civil', 'construction', 'btp'],
    'chimiste': ['chimie', 'laboratoire', 'labo'],
    'aeronautique': ['aéronautique', 'avion', 'aviation', 'pilote'],
    
    // Legal/Admin
    'avocat': ['juriste', 'notaire', 'juridique', 'droit', 'lawyer'],
    'comptable': ['financier', 'gestionnaire', 'auditeur', 'expert-comptable'],
    'secretaire': ['secrétaire', 'assistant', 'administratif', 'office'],
    'notaire': ['avocat', 'juriste', 'droit'],
    'huissier': ['justice', 'juridique'],
    'fonctionnaire': ['administration', 'état', 'etat', 'public'],
    
    // Sports/Coaching
    'coach': ['entraîneur', 'entraineur', 'formateur', 'préparateur', 'preparateur'],
    'sportif': ['coach', 'entraîneur', 'entraineur', 'athlète', 'athlete'],
    'entraineur': ['entraîneur', 'coach', 'préparateur'],
    'preparateur': ['préparateur', 'coach', 'fitness'],
    
    // Transport
    'pilote': ['aviateur', 'avion', 'aviation', 'aéronautique'],
    'chauffeur': ['conducteur', 'routier', 'transport', 'livreur'],
    'conducteur': ['chauffeur', 'train', 'bus', 'tram'],
    
    // Science
    'chercheur': ['scientifique', 'recherche', 'laboratoire', 'labo'],
    'scientifique': ['chercheur', 'science', 'recherche'],
    'biologiste': ['biologie', 'labo', 'laboratoire'],
    'geologue': ['géologue', 'geologie', 'géologie', 'terre'],
    'meteorologue': ['météorologue', 'météo', 'meteo', 'climat'],
    'oceanographe': ['océanographe', 'mer', 'ocean', 'océan', 'marin'],
    'astronome': ['astronomie', 'espace', 'étoiles', 'etoiles'],
    'statisticien': ['statistiques', 'stats', 'data'],
    
    // Hospitality/Tourism
    'cuisinier': ['chef cuisinier', 'cook', 'cuisine', 'resto', 'restaurant'],
    'patissier': ['pâtissier', 'boulanger', 'patisserie', 'pâtisserie'],
    'serveur': ['serveuse', 'restauration', 'horeca'],
    'hotelier': ['hôtelier', 'hotel', 'hôtel', 'reception'],
    'guide': ['tourisme', 'voyage', 'guide touristique'],
    'organisateur': ['événement', 'evenement', 'event', 'wedding planner'],
    
    // Agriculture
    'agriculteur': ['fermier', 'agriculture', 'paysan', 'cultivateur'],
    'eleveur': ['éleveur', 'ferme', 'animaux', 'betail', 'bétail'],
    'jardinier': ['paysagiste', 'horticulture', 'espaces verts'],
    'paysagiste': ['jardinier', 'jardin', 'espaces verts'],
    'oenologue': ['œnologue', 'vin', 'vigne', 'viticulteur'],
    
    // Dutch terms (for NL users)
    'verpleegkundige': ['infirmier', 'infirmière', 'soignant'],
    'arts': ['médecin', 'docteur'],
    'leraar': ['professeur', 'enseignant'],
    'ontwikkelaar': ['développeur', 'programmeur'],
    'ingenieur': ['ingénieur'],
    'architect': ['architecte'],
    'tandarts': ['dentiste'],
    'advocaat': ['avocat'],
    'accountant': ['comptable'],
  };

  // Get expanded search terms including synonyms
  const getExpandedSearchTerms = (term) => {
    const normalizedTerm = normalizeText(term);
    const originalTerm = term.toLowerCase();
    const terms = new Set([originalTerm, normalizedTerm]);
    
    // Only expand synonyms for terms of 3+ characters to avoid too many matches
    if (normalizedTerm.length < 3) {
      return [...terms];
    }
    
    // Add synonyms - use exact key match for precision
    Object.entries(synonyms).forEach(([key, values]) => {
      const normalizedKey = normalizeText(key);
      
      // Check if search term exactly matches synonym key (or is the start of it)
      if (normalizedKey === normalizedTerm || 
          normalizedKey.startsWith(normalizedTerm) ||
          normalizedTerm.startsWith(normalizedKey)) {
        values.forEach(v => {
          terms.add(v.toLowerCase());
          terms.add(normalizeText(v));
        });
      }
      
      // Also check if search term matches any synonym value exactly
      values.forEach(value => {
        const normalizedValue = normalizeText(value);
        if (normalizedValue === normalizedTerm ||
            normalizedValue.startsWith(normalizedTerm) ||
            normalizedTerm.startsWith(normalizedValue)) {
          terms.add(key.toLowerCase());
          terms.add(normalizedKey);
        }
      });
    });
    
    return [...terms];
  };

  const filterCareers = () => {
    let filtered = [...careers];

    // Filter by favorites only if enabled
    if (showFavoritesOnly) {
      filtered = filtered.filter(career => favorites.includes(career.id));
    }

    // Filter by search term with synonyms and accent-insensitive matching
    if (searchTerm) {
      const searchTerms = getExpandedSearchTerms(searchTerm);
      const primaryTerm = normalizeText(searchTerm);
      
      filtered = filtered.filter(career => {
        // Normalize all searchable fields
        const titleNorm = normalizeText(career.title);
        const descNorm = normalizeText(career.description);
        const skillsNorm = career.skills?.map(s => normalizeText(s)) || [];
        const domainsNorm = career.domains?.map(d => normalizeText(d)) || [];
        const envNorm = normalizeText(career.work_environment || '');
        const studiesNorm = normalizeText(career.studies || '');
        const typicalDayNorm = normalizeText(career.typical_day || '');
        
        // Title matching is the most important - check with all synonyms
        const titleMatch = searchTerms.some(term => {
          const termNorm = normalizeText(term);
          return titleNorm.includes(termNorm);
        });
        
        if (titleMatch) return true;
        
        // For other fields, only use the primary search term (not synonyms)
        // to avoid too many false positives
        
        // Check description with primary term only
        if (descNorm.includes(primaryTerm)) return true;
        
        // Check skills with primary term only
        if (skillsNorm.some(s => s.includes(primaryTerm))) return true;
        
        // Check domains
        if (domainsNorm.some(d => d.includes(primaryTerm))) return true;
        
        // Check work environment
        if (envNorm.includes(primaryTerm)) return true;
        
        // Check studies
        if (studiesNorm.includes(primaryTerm)) return true;
        
        // Check typical day
        if (typicalDayNorm.includes(primaryTerm)) return true;
        
        // Check if career ID is in the interest-based results
        if (interestCareerIds.includes(career.id)) return true;
        
        return false;
      });
    }

    // Filter by domain
    if (selectedDomain !== 'all') {
      filtered = filtered.filter(career =>
        career.domains.includes(selectedDomain)
      );
    }

    setFilteredCareers(filtered);
  };

  // Handle suggestion selection
  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion.term);
    setShowSuggestions(false);
  };

  const getDomainColor = (domain) => {
    const colors = {
      health: 'bg-red-100 text-red-700',
      technology: 'bg-blue-100 text-blue-700',
      technical: 'bg-amber-100 text-amber-700',
      science: 'bg-green-100 text-green-700',
      creativity: 'bg-pink-100 text-pink-700',
      education: 'bg-indigo-100 text-indigo-700',
      social: 'bg-purple-100 text-purple-700',
      business: 'bg-orange-100 text-orange-700',
      administration: 'bg-gray-100 text-gray-700',
      sports: 'bg-emerald-100 text-emerald-700',
    };
    return colors[domain] || 'bg-gray-100 text-gray-700';
  };

  const getDomainLabel = (domain) => {
    const labels = {
      fr: {
        health: 'Santé', technology: 'Tech', technical: 'Technique',
        science: 'Science', creativity: 'Créatif', education: 'Éducation',
        social: 'Social', business: 'Business', administration: 'Admin',
        sports: 'Sport & Bien-être'
      },
      nl: {
        health: 'Gezondheid', technology: 'Tech', technical: 'Technisch',
        science: 'Wetenschap', creativity: 'Creatief', education: 'Onderwijs',
        social: 'Sociaal', business: 'Business', administration: 'Admin',
        sports: 'Sport & Bien-être'
      }
    };
    return labels[language]?.[domain] || domain;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#EBEBEB] to-[#F3E5F5]">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-[#1D1D1D] mb-4">
            {language === 'fr' ? 'Explorer les métiers' : 'Ontdek beroepen'}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {language === 'fr' 
              ? 'Parcourez notre base de données de 194 métiers en Belgique et découvrez celui qui vous correspond.'
              : 'Blader door onze database van 194 beroepen in België en ontdek welke bij je past.'}
          </p>
        </div>
        
        {/* Compare Mode Toggle & Favorites Toggle */}
        <div className="flex justify-center gap-3 mb-6 flex-wrap">
          {/* Favorites Toggle */}
          <button
            onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
              showFavoritesOnly 
                ? 'bg-[#FB6823] text-white shadow-lg' 
                : 'bg-white text-[#FB6823] border-2 border-[#FB6823] hover:bg-[#FFF3E0]'
            }`}
            data-testid="favorites-toggle"
          >
            <Heart className={`w-5 h-5 ${showFavoritesOnly ? 'fill-white' : ''}`} />
            {showFavoritesOnly 
              ? (language === 'fr' ? `Mes favoris (${favorites.length})` : `Mijn favorieten (${favorites.length})`)
              : (language === 'fr' ? `Mes favoris (${favorites.length})` : `Mijn favorieten (${favorites.length})`)
            }
          </button>
          
          {/* Compare Mode Toggle */}
          <button
            onClick={() => {
              setCompareMode(!compareMode);
              if (compareMode) setSelectedForCompare([]);
            }}
            className={`flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
              compareMode 
                ? 'bg-[#A4187A] text-white shadow-lg' 
                : 'bg-white text-[#A4187A] border-2 border-[#A4187A] hover:bg-[#F3E5F5]'
            }`}
            data-testid="compare-mode-toggle"
          >
            <Scale className="w-5 h-5" />
            {compareMode 
              ? (language === 'fr' ? 'Mode comparaison activé' : 'Vergelijkingsmodus actief')
              : (language === 'fr' ? 'Comparer des métiers' : 'Beroepen vergelijken')
            }
          </button>
        </div>
        
        {/* Compare Selection Bar (when in compare mode) */}
        <AnimatePresence>
          {compareMode && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-gradient-to-r from-[#A4187A] to-[#8E24AA] rounded-2xl p-4 mb-6 text-white"
            >
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Scale className="w-6 h-6" />
                  <div>
                    <p className="font-semibold">
                      {language === 'fr' 
                        ? `${selectedForCompare.length}/3 métiers sélectionnés`
                        : `${selectedForCompare.length}/3 beroepen geselecteerd`
                      }
                    </p>
                    <p className="text-sm text-white/70">
                      {language === 'fr' 
                        ? 'Clique sur les métiers pour les ajouter'
                        : 'Klik op beroepen om toe te voegen'
                      }
                    </p>
                  </div>
                </div>
                
                {/* Selected careers chips */}
                <div className="flex flex-wrap gap-2">
                  {selectedForCompare.map(career => (
                    <span
                      key={career.id}
                      className="flex items-center gap-1 px-3 py-1 bg-white/20 rounded-full text-sm"
                    >
                      {career.title}
                      <button
                        onClick={() => setSelectedForCompare(prev => prev.filter(c => c.id !== career.id))}
                        className="hover:bg-white/20 rounded-full p-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
                
                {/* Compare button */}
                <button
                  onClick={() => navigate('/compare', { state: { careers: selectedForCompare } })}
                  disabled={selectedForCompare.length < 2}
                  className={`px-6 py-2 rounded-xl font-semibold transition-all ${
                    selectedForCompare.length >= 2
                      ? 'bg-[#FB6823] text-white hover:bg-[#e66e00]'
                      : 'bg-white/20 text-white/50 cursor-not-allowed'
                  }`}
                  data-testid="go-to-compare-btn"
                >
                  {language === 'fr' ? 'Comparer maintenant' : 'Nu vergelijken'}
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Discovery Mode - Interest Cards */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-[#FB6823]" />
            <h2 className="text-lg font-bold text-[#1D1D1D]">
              {language === 'fr' ? 'Explore par intérêt' : 'Ontdek op interesse'}
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {[
              { id: 'creativity', icon: Palette, label: language === 'fr' ? '🎨 Créer' : '🎨 Creëren', keywords: 'dessiner', color: 'from-pink-400 to-purple-500' },
              { id: 'technology', icon: Laptop, label: language === 'fr' ? '💻 Tech' : '💻 Tech', keywords: 'programmer', color: 'from-blue-400 to-indigo-500' },
              { id: 'health', icon: Stethoscope, label: language === 'fr' ? '🩺 Soigner' : '🩺 Verzorgen', keywords: 'soigner', color: 'from-green-400 to-emerald-500' },
              { id: 'social', icon: Users, label: language === 'fr' ? '🤝 Aider' : '🤝 Helpen', keywords: 'aider', color: 'from-yellow-400 to-orange-500' },
              { id: 'education', icon: GraduationCap, label: language === 'fr' ? '📚 Former' : '📚 Opleiden', keywords: 'enseigner', color: 'from-teal-400 to-cyan-500' },
              { id: 'security', icon: Shield, label: language === 'fr' ? '🛡️ Protéger' : '🛡️ Beschermen', keywords: 'policier', color: 'from-slate-500 to-gray-600' },
              { id: 'sports', icon: Dumbbell, label: language === 'fr' ? '💪 Sport' : '💪 Sport', keywords: 'sport', color: 'from-red-400 to-rose-500' },
              { id: 'technical', icon: Wrench, label: language === 'fr' ? '🔧 Réparer' : '🔧 Repareren', keywords: 'réparer', color: 'from-amber-500 to-yellow-600' },
              { id: 'science', icon: FlaskConical, label: language === 'fr' ? '🔬 Science' : '🔬 Wetenschap', keywords: 'recherche', color: 'from-violet-400 to-purple-600' },
              { id: 'business', icon: Building2, label: language === 'fr' ? '💼 Business' : '💼 Business', keywords: 'entreprise', color: 'from-emerald-500 to-green-600' },
            ].map((interest) => {
              const Icon = interest.icon;
              const isSelected = activeDiscoveryCard === interest.id;
              return (
                <button
                  key={interest.id}
                  onClick={async () => {
                    if (isSelected) {
                      // Deselect - clear everything
                      setActiveDiscoveryCard(null);
                      setSearchTerm('');
                      setInterestCareerIds([]);
                      setSelectedDomain('all');
                    } else {
                      // Select - search by this interest keyword
                      setActiveDiscoveryCard(interest.id);
                      setSearchTerm(interest.keywords);
                      setSelectedDomain('all'); // Reset domain filter
                      
                      // Trigger interest-based search
                      try {
                        const response = await axios.get(`${API}/search/by-interest/${language}?interest=${encodeURIComponent(interest.keywords)}`);
                        if (response.data.career_ids && response.data.career_ids.length > 0) {
                          setInterestCareerIds(response.data.career_ids);
                        }
                      } catch (err) {
                        console.log('Interest search error:', err);
                      }
                    }
                  }}
                  className={`relative p-4 rounded-xl transition-all duration-300 transform hover:scale-105 ${
                    isSelected 
                      ? `bg-gradient-to-br ${interest.color} text-white shadow-lg ring-2 ring-offset-2 ring-[#A4187A]`
                      : 'bg-white border-2 border-gray-100 hover:border-[#A4187A] hover:shadow-md text-gray-700 hover:text-[#A4187A]'
                  }`}
                  data-testid={`discovery-${interest.id}`}
                >
                  <div className="flex flex-col items-center gap-2">
                    <span className="text-2xl">{interest.label.split(' ')[0]}</span>
                    <span className={`text-sm font-medium ${isSelected ? 'text-white' : ''}`}>
                      {interest.label.split(' ')[1]}
                    </span>
                  </div>
                  {isSelected && (
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-white rounded-full flex items-center justify-center shadow">
                      <Check className="w-3 h-3 text-[#A4187A]" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search Input with Autocomplete */}
            <div className="flex-1 relative" ref={searchRef}>
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={language === 'fr' 
                  ? 'Rechercher un métier, une activité (dessiner, aider, programmer...)' 
                  : 'Zoek een beroep, activiteit (tekenen, helpen, programmeren...)'}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => searchTerm.length >= 2 && setShowSuggestions(true)}
                className="w-full pl-12 pr-10 py-3 border-2 border-gray-200 rounded-xl focus:border-[#A4187A] focus:outline-none transition-colors"
                data-testid="career-search-input"
              />
              {searchTerm && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setInterestCareerIds([]);
                    setSuggestions([]);
                    setActiveDiscoveryCard(null);
                  }}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
              
              {/* Autocomplete Suggestions Dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <div 
                  ref={suggestionsRef}
                  className="absolute z-50 top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden"
                >
                  <div className="px-4 py-2 bg-gradient-to-r from-[#F3E5F5] to-[#FFF3E0] border-b border-gray-100">
                    <p className="text-xs font-medium text-gray-600 flex items-center gap-1">
                      <Sparkles className="w-3 h-3 text-[#FB6823]" />
                      {language === 'fr' ? 'Suggestions d\'intérêts' : 'Interesse suggesties'}
                    </p>
                  </div>
                  {suggestions.map((suggestion, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSuggestionClick(suggestion)}
                      className="w-full px-4 py-3 text-left hover:bg-[#F3E5F5] transition-colors flex items-center justify-between group"
                      data-testid={`suggestion-${idx}`}
                    >
                      <span className="font-medium text-[#1D1D1D] group-hover:text-[#A4187A]">
                        {suggestion.term}
                      </span>
                      <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-500 group-hover:bg-[#A4187A] group-hover:text-white">
                        {suggestion.category}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Domain Filter */}
            <div className="relative">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 px-6 py-3 bg-[#F3E5F5] text-[#A4187A] rounded-xl font-medium hover:bg-[#E1BEE7] transition-colors"
                data-testid="filter-toggle"
              >
                <Filter className="w-5 h-5" />
                {language === 'fr' ? 'Filtrer' : 'Filteren'}
                <ChevronDown className={`w-4 h-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </button>
            </div>
          </div>

          {/* Filter Options */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-sm font-medium text-gray-600 mb-3">
                {language === 'fr' ? 'Domaine professionnel' : 'Professioneel domein'}
              </p>
              <div className="flex flex-wrap gap-2">
                {domains.map((domain) => (
                  <button
                    key={domain.id}
                    onClick={() => setSelectedDomain(domain.id)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                      selectedDomain === domain.id
                        ? 'bg-[#A4187A] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    data-testid={`filter-domain-${domain.id}`}
                  >
                    {domain.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Results Count with Interest Indicator */}
          <div className="mt-4 flex items-center gap-3 text-sm text-gray-500">
            <span>
              {filteredCareers.length} {language === 'fr' ? 'métiers trouvés' : 'beroepen gevonden'}
            </span>
            {interestCareerIds.length > 0 && searchTerm && (
              <span className="flex items-center gap-1 px-2 py-1 bg-gradient-to-r from-[#F3E5F5] to-[#FFF3E0] rounded-full text-xs font-medium text-[#A4187A]">
                <Sparkles className="w-3 h-3 text-[#FB6823]" />
                {language === 'fr' 
                  ? `${interestCareerIds.length} liés à "${searchTerm}"` 
                  : `${interestCareerIds.length} gerelateerd aan "${searchTerm}"`}
              </span>
            )}
          </div>
        </div>

        {/* Career Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#A4187A]"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCareers.map((career) => {
              const isSelectedForCompare = selectedForCompare.some(c => c.id === career.id);
              
              const toggleCompareSelection = () => {
                if (isSelectedForCompare) {
                  setSelectedForCompare(prev => prev.filter(c => c.id !== career.id));
                } else if (selectedForCompare.length < 3) {
                  setSelectedForCompare(prev => [...prev, career]);
                }
              };
              
              return (
              <div
                key={career.id}
                id={`career-card-${career.id}`}
                className={`bg-white rounded-2xl shadow-md overflow-hidden transition-all duration-300 hover:shadow-xl ${
                  expandedCareer === career.id ? 'ring-2 ring-[#A4187A]' : ''
                } ${isSelectedForCompare ? 'ring-2 ring-[#FB6823] bg-orange-50/30' : ''}`}
                data-testid={`career-card-${career.id}`}
              >
                {/* Compare Selection Checkbox (when in compare mode) */}
                {compareMode && (
                  <div 
                    className={`px-4 py-2 flex items-center gap-2 cursor-pointer transition-colors ${
                      isSelectedForCompare ? 'bg-[#FB6823] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    onClick={toggleCompareSelection}
                  >
                    {isSelectedForCompare ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <div className="w-4 h-4 border-2 border-current rounded" />
                    )}
                    <span className="text-sm font-medium">
                      {isSelectedForCompare 
                        ? (language === 'fr' ? 'Sélectionné' : 'Geselecteerd')
                        : (language === 'fr' ? 'Ajouter à la comparaison' : 'Toevoegen aan vergelijking')
                      }
                    </span>
                  </div>
                )}
                
                {/* Card Header */}
                <div className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-lg font-bold text-[#1D1D1D] leading-tight flex-1">
                      {career.title}
                    </h3>
                    <div className="flex items-center gap-1">
                      {/* Favorite Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFavorite(career);
                        }}
                        className={`p-1.5 rounded-full transition-all ${
                          isFavorite(career.id) 
                            ? 'text-red-500 bg-red-50 hover:bg-red-100' 
                            : 'text-gray-400 hover:text-red-500 hover:bg-red-50'
                        }`}
                        data-testid={`favorite-btn-${career.id}`}
                        title={language === 'fr' ? 'Ajouter aux favoris' : 'Toevoegen aan favorieten'}
                      >
                        <Heart className={`w-5 h-5 ${isFavorite(career.id) ? 'fill-current' : ''}`} />
                      </button>
                      {/* Expand Button */}
                      <button
                        onClick={() => {
                          const newExpanded = expandedCareer === career.id ? null : career.id;
                          setExpandedCareer(newExpanded);
                          // Scroll to expanded content after a short delay
                          if (newExpanded) {
                            setTimeout(() => {
                              const element = document.getElementById(`career-card-${career.id}`);
                              if (element) {
                                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                              }
                            }, 100);
                          }
                        }}
                        className="p-1 text-gray-400 hover:text-[#A4187A] transition-colors"
                        title={language === 'fr' ? 'Voir les détails' : 'Bekijk details'}
                      >
                        {expandedCareer === career.id ? (
                          <ChevronUp className="w-5 h-5" />
                        ) : (
                          <ChevronDown className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Domain Tags */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {career.domains.map((domain) => (
                      <span
                        key={domain}
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getDomainColor(domain)}`}
                      >
                        {getDomainLabel(domain)}
                      </span>
                    ))}
                  </div>

                  {/* Description */}
                  <p className="text-gray-600 text-sm line-clamp-2">
                    {career.description}
                  </p>
                </div>

                {/* Expanded Content */}
                {expandedCareer === career.id && (
                  <div className="px-6 pb-6 border-t border-gray-100 pt-4 animate-fadeIn">
                    {/* Salary Range - NEW */}
                    {career.salary_range && (
                      <div className="mb-4">
                        <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                          <svg className="w-4 h-4 text-[#FB6823]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {language === 'fr' ? 'Salaire indicatif' : 'Indicatief salaris'}
                        </div>
                        <p className="text-sm text-gray-600 pl-6 font-medium text-[#A4187A]">
                          {career.salary_range}
                        </p>
                      </div>
                    )}

                    {/* Studies Required - NEW */}
                    {career.studies && (
                      <div className="mb-4">
                        <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                          <svg className="w-4 h-4 text-[#FB6823]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path d="M12 14l9-5-9-5-9 5 9 5z" />
                            <path d="M12 14l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14zm-4 6v-7.5l4-2.222" />
                          </svg>
                          {language === 'fr' ? 'Formation requise' : 'Vereiste opleiding'}
                        </div>
                        <p className="text-sm text-gray-600 pl-6">
                          {career.studies}
                        </p>
                      </div>
                    )}

                    {/* Typical Day - NEW */}
                    {career.typical_day && (
                      <div className="mb-4">
                        <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                          <svg className="w-4 h-4 text-[#FB6823]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {language === 'fr' ? 'Journée type' : 'Typische dag'}
                        </div>
                        <p className="text-sm text-gray-600 pl-6 italic">
                          {career.typical_day}
                        </p>
                      </div>
                    )}

                    {/* Work Environment */}
                    {career.work_environment && (
                      <div className="mb-4">
                        <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                          <MapPin className="w-4 h-4 text-[#FB6823]" />
                          {language === 'fr' ? 'Environnement de travail' : 'Werkomgeving'}
                        </div>
                        <p className="text-sm text-gray-600 pl-6">
                          {career.work_environment}
                        </p>
                      </div>
                    )}

                    {/* Skills */}
                    {career.skills && career.skills.length > 0 && (
                      <div className="mb-4">
                        <div className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                          <Star className="w-4 h-4 text-[#FB6823]" />
                          {language === 'fr' ? 'Compétences clés' : 'Belangrijke vaardigheden'}
                        </div>
                        <div className="flex flex-wrap gap-2 pl-6">
                          {career.skills.map((skill, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-[#F3E5F5] text-[#A4187A] rounded text-xs"
                            >
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* CTA Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3 mt-4">
                      <button
                        onClick={() => {
                          setModalCareer(career);
                          setShowModal(true);
                        }}
                        className="flex-1 py-3 bg-[#FB6823] text-white rounded-xl font-medium hover:bg-[#e66e00] transition-colors flex items-center justify-center gap-2"
                      >
                        <Eye className="w-4 h-4" />
                        {language === 'fr' ? 'Voir les détails' : 'Bekijk details'}
                      </button>
                      <button
                        onClick={() => navigate('/questionnaire')}
                        className="flex-1 py-3 bg-gradient-to-r from-[#A4187A] to-[#8B2FC9] text-white rounded-xl font-medium hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
                      >
                        <Briefcase className="w-4 h-4" />
                        {language === 'fr' ? 'Faire le test' : 'Doe de test'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredCareers.length === 0 && (
          <div className="text-center py-20">
            <div className="w-20 h-20 bg-[#F3E5F5] rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-10 h-10 text-[#A4187A]" />
            </div>
            <h3 className="text-xl font-bold text-[#1D1D1D] mb-2">
              {language === 'fr' ? 'Aucun métier trouvé' : 'Geen beroepen gevonden'}
            </h3>
            <p className="text-gray-600">
              {language === 'fr' 
                ? 'Essayez de modifier vos critères de recherche'
                : 'Probeer je zoekcriteria aan te passen'}
            </p>
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-[#A4187A] to-[#FB6823] rounded-2xl p-8 text-center text-white">
          <h2 className="text-2xl font-bold mb-4">
            {language === 'fr' 
              ? 'Tu ne sais pas par où commencer ?'
              : 'Weet je niet waar te beginnen?'}
          </h2>
          <p className="mb-6 opacity-90">
            {language === 'fr'
              ? 'Fais notre test Ikigai de 3 minutes pour découvrir les métiers qui te correspondent vraiment.'
              : 'Doe onze 3-minuten Ikigai-test om te ontdekken welke beroepen echt bij je passen.'}
          </p>
          <button
            onClick={() => navigate('/questionnaire')}
            className="px-8 py-3 bg-white text-[#A4187A] rounded-xl font-bold hover:bg-gray-100 transition-colors"
            data-testid="start-test-from-explore"
          >
            {language === 'fr' ? 'Commencer le test' : 'Start de test'}
          </button>
        </div>
      </main>

      <Footer />
      
      {/* Career Detail Modal */}
      <CareerDetailModal 
        career={modalCareer}
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setModalCareer(null);
        }}
      />
    </div>
  );
}
